/**
 * Template Gallery - Browse, compare, and use industry scenario templates
 * Always-visible compare selection + floating comparison tray
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Factory, ShoppingCart, Landmark, Monitor,
  HardHat, HeartPulse, Truck, Utensils, Star, Users, TrendingDown,
  DollarSign, Calendar, Loader2, Sparkles, Filter, Wand2,
  Scale, X, ArrowRight, Plus, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import RecommendationWizard from './RecommendationWizard';
import TemplateDetailModal from './TemplateDetailModal';
import TemplateComparisonModal from './TemplateComparisonModal';
import { apiFetch } from '../../services/api_service';


const INDUSTRIES = [
  { id: 'all', name: 'All Industries', icon: Sparkles },
  { id: 'manufacturing', name: 'Manufacturing', icon: Factory },
  { id: 'retail', name: 'Retail & Consumer', icon: ShoppingCart },
  { id: 'financial_services', name: 'Financial Services', icon: Landmark },
  { id: 'technology', name: 'Technology & SaaS', icon: Monitor },
  { id: 'construction', name: 'Construction & Real Estate', icon: HardHat },
  { id: 'healthcare', name: 'Healthcare', icon: HeartPulse },
  { id: 'logistics', name: 'Logistics & Transport', icon: Truck },
  { id: 'food_beverage', name: 'Food & Beverage', icon: Utensils },
];

const DIFF_COLORS = {
  beginner: ['text-emerald-400', 'bg-emerald-500/15', 'border-emerald-500/30'],
  intermediate: ['text-amber-400', 'bg-amber-500/15', 'border-amber-500/30'],
  advanced: ['text-red-400', 'bg-red-500/15', 'border-red-500/30'],
};

function fmt$(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
}

export default function TemplateGalleryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [industry, setIndustry] = useState('all');
  const [difficulty, setDifficulty] = useState('all');
  const [search, setSearch] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    apiFetch(`/v1/simulations/templates`)
      .then(d => { if (d && !d._error) setTemplates(d.templates || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return templates.filter(t => {
      if (industry !== 'all' && t.industry !== industry) return false;
      if (difficulty !== 'all' && t.difficulty !== difficulty) return false;
      if (search && !t.templateName.toLowerCase().includes(search.toLowerCase()) && !t.templateDescription.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [templates, industry, difficulty, search]);

  const toggleCompare = (id) => {
    setCompareIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) {
        toast({ title: 'Maximum 3 templates', description: 'Remove one to add another', variant: 'destructive' });
        return prev;
      }
      return [...prev, id];
    });
  };

  const useTemplate = async (templateId) => {
    setCreating(templateId);
    try {
      const res = await apiFetch(`/v1/simulations/from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId }),
      });
      if (res && !res._error) {
        toast({ title: 'Scenario Created from Template' });
        navigate(`/simulations/${res.scenario.scenarioId}`);
      }
    } catch {
      toast({ title: 'Error creating scenario', variant: 'destructive' });
    } finally {
      setCreating(null);
    }
  };

  // Get template objects for compare tray
  const compareTemplates = useMemo(() => {
    return compareIds.map(id => templates.find(t => t.templateId === id)).filter(Boolean);
  }, [compareIds, templates]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-violet-400 animate-spin" /></div>;

  return (
    <div className="space-y-5 pb-28" data-testid="template-gallery-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/simulations')} className="text-[#6B6B6B] hover:text-white transition-colors" data-testid="back-to-list">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Scenario Templates</h1>
            <p className="text-xs text-[#6B6B6B]">Industry-proven strategies to kickstart your net zero journey</p>
          </div>
        </div>
        <Button variant="outline" className="border-[#2F2F2F] text-[#A0A0A0]" onClick={() => navigate('/simulations')}>
          Build From Scratch
        </Button>
      </div>

      {/* AI Recommendation CTA */}
      <button
        onClick={() => setShowWizard(true)}
        className="w-full p-4 rounded-xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 via-purple-500/5 to-blue-500/10 hover:from-violet-500/15 hover:via-purple-500/10 hover:to-blue-500/15 transition-all group"
        data-testid="ai-advisor-cta"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/20 rounded-lg group-hover:bg-violet-500/30 transition-colors">
            <Wand2 className="w-5 h-5 text-violet-400" />
          </div>
          <div className="text-left flex-1">
            <p className="text-sm font-semibold text-white">Not sure where to start? Try AI Template Advisor</p>
            <p className="text-[11px] text-[#6B6B6B]">Answer 5 quick questions and get personalized recommendations from Claude Sonnet 4.6</p>
          </div>
          <Sparkles className="w-5 h-5 text-violet-400/50 group-hover:text-violet-400 transition-colors" />
        </div>
      </button>

      {showWizard && (
        <RecommendationWizard
          onClose={() => setShowWizard(false)}
          onUseTemplate={(templateId) => { setShowWizard(false); useTemplate(templateId); }}
        />
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-[#111111] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder:text-[#3F3F3F] focus:outline-none focus:border-violet-500/50"
            data-testid="template-search"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-[#6B6B6B]" />
          {['all', 'beginner', 'intermediate', 'advanced'].map(d => (
            <button
              key={d}
              onClick={() => setDifficulty(d)}
              className={`px-2.5 py-1.5 text-xs rounded-lg transition-colors ${difficulty === d ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' : 'bg-[#1A1A1A] text-[#6B6B6B] border border-transparent hover:text-[#A0A0A0]'}`}
              data-testid={`diff-filter-${d}`}
            >
              {d === 'all' ? 'All' : d}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Industry Sidebar */}
        <div className="col-span-3">
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-3 sticky top-4">
            <h3 className="text-xs font-semibold text-[#A0A0A0] mb-2 px-2">INDUSTRIES</h3>
            <div className="space-y-0.5">
              {INDUSTRIES.map(ind => {
                const Icon = ind.icon;
                const count = templates.filter(t => ind.id === 'all' || t.industry === ind.id).length;
                return (
                  <button
                    key={ind.id}
                    onClick={() => setIndustry(ind.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                      industry === ind.id
                        ? 'bg-violet-500/15 text-violet-400 font-medium'
                        : 'text-[#6B6B6B] hover:text-[#A0A0A0] hover:bg-[#1A1A1A]'
                    }`}
                    data-testid={`industry-${ind.id}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1 truncate">{ind.name}</span>
                    <span className="text-[10px] text-[#3F3F3F]">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Template Grid */}
        <div className="col-span-9">
          <p className="text-xs text-[#6B6B6B] mb-3">{filtered.length} template{filtered.length !== 1 ? 's' : ''}</p>
          {filtered.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#2F2F2F] rounded-xl">
              <Search className="w-8 h-8 mx-auto mb-2 text-[#2F2F2F]" />
              <p className="text-sm text-[#6B6B6B]">No templates match your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {filtered.map(tmpl => (
                <TemplateCard
                  key={tmpl.templateId}
                  template={tmpl}
                  onUse={useTemplate}
                  onView={setSelectedTemplate}
                  creating={creating}
                  isCompareSelected={compareIds.includes(tmpl.templateId)}
                  onToggleCompare={toggleCompare}
                  compareCount={compareIds.length}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <TemplateDetailModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onUse={(templateId) => { setSelectedTemplate(null); useTemplate(templateId); }}
          onAddToCompare={(templateId) => {
            toggleCompare(templateId);
            setSelectedTemplate(null);
          }}
          isInCompare={compareIds.includes(selectedTemplate.templateId)}
        />
      )}

      {/* Template Comparison Modal */}
      {showComparison && compareIds.length >= 2 && (
        <TemplateComparisonModal
          templateIds={compareIds}
          onClose={() => setShowComparison(false)}
          onUse={(templateId) => { setShowComparison(false); setCompareIds([]); useTemplate(templateId); }}
        />
      )}

      {/* ─── Floating Comparison Tray ─── */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
          <div className="max-w-5xl mx-auto px-4 pb-4 pointer-events-auto">
            <div
              className="bg-[#0C0C0C]/95 backdrop-blur-xl border border-[#2A2A2A] rounded-2xl shadow-2xl shadow-violet-500/5 p-4"
              data-testid="comparison-tray"
            >
              <div className="flex items-center gap-4">
                {/* Slot indicators */}
                <div className="flex items-center gap-2 flex-1">
                  {[0, 1, 2].map(i => {
                    const tmpl = compareTemplates[i];
                    if (tmpl) {
                      const [tc, bg, bc] = DIFF_COLORS[tmpl.difficulty] || DIFF_COLORS.intermediate;
                      return (
                        <div key={i} className="flex items-center gap-2 bg-[#111] border border-[#2A2A2A] rounded-xl px-3 py-2 min-w-0 flex-1 group" data-testid={`tray-slot-${i}`}>
                          <div className={`w-6 h-6 rounded-full ${bg} border ${bc} flex items-center justify-center shrink-0`}>
                            <span className={`text-[10px] font-bold ${tc}`}>{i + 1}</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-white truncate">{tmpl.templateName}</p>
                            <p className="text-[9px] text-[#6B6B6B] truncate">{tmpl.industry?.replace(/_/g, ' ')} · {tmpl.difficulty}</p>
                          </div>
                          <button
                            onClick={() => toggleCompare(tmpl.templateId)}
                            className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-500/10 transition-all"
                            data-testid={`tray-remove-${i}`}
                          >
                            <X className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div key={i} className="flex items-center justify-center bg-[#111] border border-dashed border-[#2A2A2A] rounded-xl px-3 py-2 min-w-0 flex-1 h-[52px]" data-testid={`tray-slot-${i}`}>
                        <span className="text-[10px] text-[#4A4A4A]">Select template {i + 1}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setCompareIds([])}
                    className="text-[10px] text-[#6B6B6B] hover:text-white transition-colors px-2 py-1"
                    data-testid="clear-compare"
                  >
                    Clear All
                  </button>
                  <Button
                    disabled={compareIds.length < 2}
                    className="bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40 disabled:cursor-not-allowed gap-1.5 shadow-lg shadow-violet-500/20"
                    onClick={() => setShowComparison(true)}
                    data-testid="open-comparison-btn"
                  >
                    <Scale className="w-4 h-4" />
                    Compare & Analyze ({compareIds.length})
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateCard({ template: t, onUse, onView, creating, isCompareSelected, onToggleCompare, compareCount }) {
  const [textC, bgC, borderC] = DIFF_COLORS[t.difficulty] || DIFF_COLORS.intermediate;
  const er = t.expectedResults || {};
  const bench = t.industryBenchmark || {};
  const isCreating = creating === t.templateId;
  const costRange = er.financialSummary?.totalCostRange;

  return (
    <div
      className={`bg-[#111111] border rounded-xl hover:border-violet-500/30 transition-all group relative ${
        isCompareSelected ? 'border-violet-500 ring-1 ring-violet-500/20 bg-violet-500/[0.03]' : 'border-[#1F1F1F]'
      }`}
      data-testid={`template-${t.templateId}`}
    >
      {/* Compare toggle - always visible */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleCompare(t.templateId); }}
        className={`absolute top-3 left-3 z-10 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-all ${
          isCompareSelected
            ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30'
            : 'bg-[#0D0D0D]/80 text-[#6B6B6B] border border-[#2A2A2A] opacity-0 group-hover:opacity-100 hover:text-violet-400 hover:border-violet-500/30'
        }`}
        data-testid={`compare-toggle-${t.templateId}`}
      >
        {isCompareSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
        {isCompareSelected ? 'Added' : 'Compare'}
      </button>

      <div className="p-4 cursor-pointer" onClick={() => onView(t)}>
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0 mr-8">
            <h3 className="text-sm font-semibold text-white group-hover:text-violet-400 transition-colors truncate">{t.templateName}</h3>
            <p className="text-[10px] text-[#6B6B6B] mt-0.5">{t.industry.replace(/_/g, ' ').toUpperCase()} &middot; {t.useCase.replace(/_/g, ' ')}</p>
          </div>
          <Badge className={`${bgC} ${textC} border ${borderC} text-[10px] shrink-0`}>{t.difficulty}</Badge>
        </div>

        <p className="text-xs text-[#6B6B6B] line-clamp-2 mb-3">{t.templateDescription}</p>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 bg-[#0D0D0D] rounded-lg">
            <TrendingDown className="w-3 h-3 mx-auto mb-1 text-emerald-400" />
            <p className="text-lg font-bold text-emerald-400">{er.emissionsReduction?.percentageReduction || 0}%</p>
            <p className="text-[9px] text-[#6B6B6B]">Reduction</p>
          </div>
          <div className="text-center p-2 bg-[#0D0D0D] rounded-lg">
            <DollarSign className="w-3 h-3 mx-auto mb-1 text-blue-400" />
            <p className="text-sm font-bold text-blue-400">
              {costRange ? `${fmt$(costRange.min)}-${fmt$(costRange.max)}` : 'N/A'}
            </p>
            <p className="text-[9px] text-[#6B6B6B]">Investment</p>
          </div>
          <div className="text-center p-2 bg-[#0D0D0D] rounded-lg">
            <Calendar className="w-3 h-3 mx-auto mb-1 text-purple-400" />
            <p className="text-lg font-bold text-purple-400">{er.timeline?.netZeroAchieved || t.target?.targetYear || '2030'}</p>
            <p className="text-[9px] text-[#6B6B6B]">Target</p>
          </div>
        </div>

        {/* Benchmark */}
        {bench.peerAdoptionRate && (
          <div className="bg-[#0D0D0D] rounded-lg p-2.5 mb-3">
            <div className="flex items-center justify-between text-[10px] mb-1.5">
              <span className="text-[#6B6B6B]">Industry Adoption</span>
              <span className="text-[#A0A0A0] font-medium">{Math.round(bench.peerAdoptionRate * 100)}% of peers</span>
            </div>
            <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
              <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${bench.peerAdoptionRate * 100}%` }} />
            </div>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {(t.tags || []).slice(0, 3).map(tag => (
            <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-[#1A1A1A] text-[#6B6B6B] rounded">{tag}</span>
          ))}
          {(t.tags || []).length > 3 && <span className="text-[9px] px-1.5 py-0.5 bg-[#1A1A1A] text-[#6B6B6B] rounded">+{t.tags.length - 3}</span>}
        </div>

        {/* Rating & Popularity */}
        <div className="flex items-center justify-between text-[10px] text-[#6B6B6B] mb-3">
          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span className="text-white font-medium">{t.avgRating?.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            <span>{(t.popularity || 0).toLocaleString()} companies</span>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="border-t border-[#1F1F1F] p-3">
        <button
          onClick={(e) => { e.stopPropagation(); onUse(t.templateId); }}
          disabled={isCreating}
          className="w-full py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid={`use-template-${t.templateId}`}
        >
          {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {isCreating ? 'Creating...' : 'Use This Template'}
        </button>
      </div>
    </div>
  );
}
